import { ref } from 'vue'

/**
 * Détection d'activité utilisateur — singleton global.
 *
 * Écoute les évènements de "vie" du renderer (mousemove, keydown, click,
 * scroll, touch, focus) et met à jour `lastActivityAt` au plus une fois
 * par seconde (throttle anti-jank — un mousemove peut firer ~60 fois/s).
 *
 * Utilisé par le store `agent_status` pour basculer automatiquement
 * `available` → `busy` après 3 min sans activité.
 *
 * On ne désinstalle jamais les listeners — l'app vit tant que la fenêtre
 * Electron vit, et on en veut un seul jeu pour toute l'app (sinon les
 * compteurs se contredisent entre composants).
 */

const lastActivityAt = ref<number>(Date.now())
let installed = false
let lastWrite = 0

function markActive() {
  // Throttle 1 s : suffisant pour la sémantique « l'utilisateur fait
  // quelque chose » sans réveiller Vue à chaque pixel de souris.
  const now = Date.now()
  if (now - lastWrite < 1000) return
  lastWrite = now
  lastActivityAt.value = now
}

function install() {
  if (installed || typeof window === 'undefined') return
  installed = true
  const opts: AddEventListenerOptions = { passive: true, capture: true }
  window.addEventListener('mousemove', markActive, opts)
  window.addEventListener('mousedown', markActive, opts)
  window.addEventListener('keydown', markActive, opts)
  window.addEventListener('wheel', markActive, opts)
  window.addEventListener('touchstart', markActive, opts)
  window.addEventListener('focus', markActive, opts)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') markActive()
  })
}

export function useUserActivity() {
  install()
  return {
    lastActivityAt,
    /** Marque manuellement une activité — utile pour les évènements non DOM
     *  (réception d'appel, changement de route, etc.). */
    markActive() {
      // Reset complet du throttle pour ces évènements explicites.
      lastWrite = 0
      markActive()
    },
    /** Secondes écoulées depuis la dernière activité détectée. */
    idleSeconds(): number {
      return Math.floor((Date.now() - lastActivityAt.value) / 1000)
    },
  }
}
